import _set from 'lodash/set'
import _get from 'lodash/get'
import _uniqueId from 'lodash/uniqueId'
import _kebabCase from 'lodash/kebabCase'
import _camelCase from 'lodash/camelCase'
import _capitalize from 'lodash/capitalize'

import { Environment } from '../enums/enums'
import { isDevelopment } from './common'
import { DEFAULT_ENV_KEY } from '../constants'
import { getLocalStorage } from './localStorage'
import { IProspectMetadata } from '../types/prospects-types'
import { IField, IPreferenceField, Option, StringKeyValuePairs } from '../types'
import { IOrganizationMetadata } from '../types/organizations-types'
import { IDealField, IDealMetadata, IPipelineStage } from '../types/deals-types'
import { IProspect, IRawProspect, IOrganization, IRawOrganization, IDeal, IRawDeal } from '../types/globals'

export function toShortLocalTime(date: Date): string {
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true }
  return date.toLocaleString([], opts)
}

export function toShortLocalDate(date: Date): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit', year: 'numeric' }
  return date.toLocaleString([], opts)
}

export function toShortLocalDateTime(timestamp?: number): string | null {
  if (!timestamp) return null

  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }
  return new Date(timestamp * 1000).toLocaleString([], opts)
}

export function withIds<T>(arr: T[] = [], prefix: string): T[] {
  return arr.map((i) => ({ ...i, id: _uniqueId(prefix) }))
}

export function isAlwaysTrue(): boolean {
  return true
}

export function capitalizeLabel(str: string): string {
  return _kebabCase(str).split('-').map(_capitalize).join(' ')
}

export function firstLetters(str: string): string | undefined {
  return str.match(/\b\w/g)?.join('').toUpperCase().slice(0, 2)
}

export function capitalizeFirstLetter(str: string): string {
  return str[0].toUpperCase() + str.slice(1)
}

export function getEnumOptions(e: StringKeyValuePairs, hasEmptyItem = false, capitalize = true): Option[] {
  const keys = Object.keys(e)
  const items = keys.map((k) => ({ label: capitalize ? capitalizeLabel(k) : k, value: e[k] }))
  return hasEmptyItem ? [{ label: '', value: '' }, ...items] : items
}

export function getEnumValues(e: StringKeyValuePairs): string[] {
  return Object.values(e)
}

export function getNumberWithOrdinal(n: number, ordinalOnly = false): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  const ordinal = s[(v - 20) % 10] || s[v] || s[0]
  return ordinalOnly ? ordinal : n + ordinal
}

export function randomIntFromInterval(min: number, max: number): number {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min)
}

export function toOrdinalShortLocaleDate(date: Date): string {
  const localeDate = toShortLocalDate(date)
  const localeDateArray = localeDate.split(' ')
  localeDateArray[1] = getNumberWithOrdinal(parseInt(localeDateArray[1])).toString() + ','
  return localeDateArray.join(' ')
}

export function numberWithCommas(num: number): string {
  const decimalNumber = num.toString().split('.')
  decimalNumber[0] = decimalNumber[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return decimalNumber.join('.')
}

export function getBearerToken(token: string): string {
  return `Bearer ${token}`
}

export const getDefaultEnv = (): Environment => {
  const env = getLocalStorage(DEFAULT_ENV_KEY)

  if (env && getEnumValues(Environment).includes(env)) return env as Environment
  else if (isDevelopment()) return Environment.staging

  return Environment.production
}

export const getApiURLAndClientID = (): { apiUrl?: string; clientId?: string } => {
  const environmentsObj = {
    [Environment.staging]: {
      apiUrl: process.env.REACT_APP_STAGING_API_URL,
      clientId: process.env.REACT_APP_STAGING_CLIENT_ID,
    },
    [Environment.production]: {
      apiUrl: process.env.REACT_APP_PRODUCTION_API_URL,
      clientId: process.env.REACT_APP_PRODUCTION_CLIENT_ID,
    },
    [Environment.demo]: {
      apiUrl: process.env.REACT_APP_DEMO_API_URL,
      clientId: process.env.REACT_APP_DEMO_CLIENT_ID,
    },
  }

  try {
    const environment = getDefaultEnv()
    return environmentsObj[environment]
  } catch (e) {
    console.error(e)
    return environmentsObj[Environment.staging]
  }
}

export const getProspect = (prospect: Partial<IRawProspect>): Partial<IProspect> => {
  const prospectObj = {} as Partial<IProspect>

  prospectObj.id = _get(prospect, 'id', undefined)
  prospectObj.email = _get(prospect, 'email', undefined)
  prospectObj.owner = _get(prospect, 'owner.ownerUser', undefined)
  prospectObj.lifecycleStage = _get(prospect, 'prospectLifecycle.lifecycleStage', undefined)
  prospectObj.lifecycleStatus = _get(prospect, 'prospectLifecycle.status', undefined)

  const identifiersArray = [
    'FullName',
    'CreateDate',
    'Company',
    'WorkPhone',
    'JobTitle',
    'FirstName',
    'LastName',
    'Country',
    'City',
    'State',
    'Zipcode',
    'MobilePhone',
    'StreetAddress',
    'StreetAddress2',
    'HomePhone',
    'LeadScore',
    'LeadGrade',
  ]

  for (const item of prospect?.standardFields ?? []) {
    const identifier = _get(item, 'field.identifier')

    if (identifiersArray.includes(identifier)) {
      _set(
        prospectObj,
        _camelCase(identifier),
        identifier !== 'CreateDate' ? item.value : new Date(item.value).toLocaleString()
      )
    }

    if (Object.keys(prospectObj).length >= 22) break
  }

  const socialPhotoField = prospect?.customFields?.find((item) => item?.field?.identifier === 'social_primary_photo__c')
  if (socialPhotoField) prospectObj.socialPhoto = socialPhotoField.value

  return prospectObj
}

export const getOrganization = (organization: Partial<IRawOrganization>): Partial<IOrganization> => {
  const organizationObj = {} as Partial<IOrganization>

  organizationObj.id = _get(organization, 'id', undefined)
  organizationObj.ownerFirstName = _get(organization, 'owner.ownerUser.firstName', undefined)
  organizationObj.ownerLastName = _get(organization, 'owner.ownerUser.lastName', undefined)
  organizationObj.ownerProfilePicture = _get(organization, 'owner.ownerUser.fileUserProfilePicture', undefined)
  organizationObj.totalProspectsCount = _get(organization, 'totalProspectsCount', 0)

  const identifiersArray = ['Name', 'CreateDate', 'Logo']

  for (const item of organization?.fields ?? []) {
    const identifier = _get(item, 'field.identifier')

    if (identifiersArray.includes(identifier)) {
      _set(
        organizationObj,
        _camelCase(identifier),
        identifier !== 'CreateDate' ? item.value : new Date(item.value).toLocaleString()
      )
    }

    if (Object.keys(organizationObj).length === 8) break
  }

  return organizationObj
}

export const getDeal = (deal: Partial<IRawDeal>): Partial<IDeal> => {
  const dealObj = {} as Partial<IDeal>

  dealObj.id = _get(deal, 'id', undefined)
  dealObj.name = _get(deal, 'info.name', undefined)
  dealObj.expectedCloseDate = _get(deal, 'info.expectedCloseDate', undefined)
  dealObj.amount = _get(deal, 'info.amount', undefined)
  dealObj.status = _get(deal, 'status', undefined)
  dealObj.pipelineName = _get(deal, 'pipeline.pipelineName', undefined)
  dealObj.stageName = _get(deal, 'pipelineStage.stageName', undefined)
  dealObj.status = _get(deal, 'status')
  dealObj.owner = _get(deal, 'owner.ownerUser')
  dealObj.prospect = _get(deal, 'prospect')
  dealObj.organization = _get(deal, 'organization')

  for (const item of deal?.fields ?? []) {
    const identifier = _get(item, 'field.identifier')
    const identifiersArray = ['Name', 'Amount', 'ExpectedCloseDate', 'Probability']

    if (identifiersArray.includes(identifier)) {
      _set(
        dealObj,
        _camelCase(identifier),
        identifier === 'ExpectedCloseDate' ? new Date(item.value).toLocaleString() : item.value
      )
    }

    if (Object.keys(dealObj).length >= 12) break
  }

  return dealObj
}

export function getProspectProfile({
  prospectRawData,
  prospectMetaData,
}: {
  prospectRawData: IRawProspect
  prospectMetaData: IProspectMetadata
}): Partial<IProspect> {
  const prospectId = _get(prospectRawData, 'id')
  const email = _get(prospectRawData, 'email')
  const owner = _get(prospectRawData, 'owner.ownerUser', undefined)
  const lifecycleStage = _get(prospectRawData, 'prospectLifecycle.lifecycleStage', undefined)
  const lifecycleStatus = _get(prospectRawData, 'prospectLifecycle.status', undefined)

  const socialPhoto = prospectRawData.customFields.find(
    (item) => item?.field?.identifier === 'social_primary_photo__c'
  )?.value

  const prospectFieldsIdentifier = [
    'FullName',
    'MobilePhone',
    'HomePhone',
    'State',
    'City',
    'LeadScore',
    'LeadGrade',
    'LastInboundActivityDate',
  ]

  const prospectInfo = prospectFieldsIdentifier.reduce((acc, curr) => {
    const fieldValue = prospectRawData.standardFields?.find((item) => item.field.identifier === curr)?.value
    return { ...acc, [_camelCase(curr)]: fieldValue }
  }, {} as Partial<IProspect>)

  const detailsFields = prospectMetaData.userPreference.data.reduce((acc, curr): IPreferenceField[] => {
    const field = {} as IPreferenceField
    const fieldName = curr.split('_')[0]
    const fieldId = parseInt(curr.split('_')[1])

    field.id = curr
    if (fieldName === 'standard') {
      field.field = prospectMetaData.standardFields.find((field) => field.id === fieldId) as IField
      field.value = prospectRawData.standardFields?.find((field) => field.field.id === fieldId)?.value
    } else if (fieldName === 'custom') {
      field.field = prospectMetaData.customFields.find((field) => field.id === fieldId) as IField

      if (field.field === undefined) {
        field.field = prospectMetaData.multiValuesCustomFields.find((field) => field.id === fieldId) as IField
        field.value = prospectRawData.multiValuesCustomFields?.find((field) => field?.field?.id === fieldId)?.values
        field.id = `multiValuesCustom_${field?.field?.id}`
      } else field.value = prospectRawData.customFields?.find((field) => field?.field?.id === fieldId)?.value
    }
    return [...acc, field]
  }, [] as IPreferenceField[])

  return {
    id: prospectId,
    owner,
    email,
    lifecycleStage,
    socialPhoto,
    lifecycleStatus,
    detailsFields,
    ...prospectInfo,
  }
}

export function filterProspectPatchResponse({
  responseData,
  cachedData,
}: {
  responseData: Partial<IRawProspect>
  cachedData: IProspect
}): Partial<IProspect> {
  const owner = _get(responseData, 'owner.ownerUser', null)
  const lifecycleStage = _get(responseData, 'prospectLifecycle.lifecycleStage', undefined)
  const lifecycleStatus = _get(responseData, 'prospectLifecycle.status', undefined)

  const fieldsIdentifierArray = [
    'City',
    'State',
    'LeadScore',
    'LeadGrade',
    'MobilePhone',
    'HomePhone',
    'Email',
    'FullName',
  ]

  const prospectInfo = fieldsIdentifierArray.reduce((acc, curr) => {
    const fieldValue = responseData.standardFields?.find((item) => item.field.identifier === curr)?.value
    return { ...acc, [_camelCase(curr)]: fieldValue }
  }, {} as Partial<IProspect>)

  const detailsFields = cachedData.detailsFields.reduce((acc, curr) => {
    const fieldName = curr.id.split('_')[0]
    const fieldId = parseInt(curr.id.split('_')[1])
    if (fieldName === 'standard') {
      curr.value = responseData.standardFields?.find((item) => item.field.id === fieldId)?.value
    }
    if (fieldName === 'custom') {
      curr.value = responseData.customFields?.find((item) => item.field.id === fieldId)?.value
    }
    if (fieldName === 'multiValuesCustom') {
      curr.value = responseData.multiValuesCustomFields?.find((item) => item.field.id === fieldId)?.values
    }
    return [...acc, curr]
  }, [] as IPreferenceField[])

  return {
    ...cachedData,
    owner,
    lifecycleStage,
    lifecycleStatus,
    ...prospectInfo,
    detailsFields: JSON.parse(JSON.stringify(detailsFields)),
  }
}

export function getDealProfile({
  dealRawData,
  dealMetadata,
}: {
  dealRawData: IRawDeal
  dealMetadata: IDealMetadata
}): Partial<IDeal> {
  const dealId = _get(dealRawData, 'id')
  const owner = _get(dealRawData, 'owner.ownerUser', undefined)
  const pipeline = _get(dealRawData, 'pipeline', {})
  _set(
    pipeline,
    'stages',
    _get(pipeline, 'stages', []).sort((a: IPipelineStage, b: IPipelineStage) => a.stageOrder - b.stageOrder)
  )

  const pipelineStage = _get(dealRawData, 'pipelineStage', undefined)
  const isClosed = _get(dealRawData, 'isClosed')
  const isWon = _get(dealRawData, 'isWon')

  const fieldIdentifiersArray = ['Name', 'Amount', 'Probability']

  const dealInfo = fieldIdentifiersArray.reduce((acc, curr) => {
    const fieldObj = {} as IDealField
    fieldObj.field = dealMetadata.fields.find((item) => item.identifier === curr) as IField
    fieldObj.value = dealRawData.fields?.find((item) => item.field.identifier === curr)?.value
    return { ...acc, [_camelCase(curr)]: fieldObj }
  }, {} as Partial<IDeal>)

  const detailsFields = dealMetadata.userPreference.data.reduce((acc, curr) => {
    const field = {} as IPreferenceField
    field.id = `field_${curr}`
    field.field = dealMetadata.fields.find((item) => item.id === curr) as IField

    if (field.field === undefined) {
      field.field = dealMetadata.multiValuesFields.find((item) => item.id === curr) as IField
      field.value = dealRawData.multiValuesFields?.find((item) => item.field.id === curr)?.values
      field.id = `multiValues_${field.field.id}`
    } else field.value = dealRawData.fields?.find((item) => item.field.id === curr)?.value

    return [...acc, field]
  }, [] as IPreferenceField[])

  return { id: dealId, pipeline, pipelineStage, owner, isClosed, isWon, detailsFields, ...dealInfo }
}

export function filterDealPatchResponse({
  responseData,
  cachedData,
}: {
  responseData: Partial<IRawDeal>
  cachedData: IDeal
}): Partial<IDeal> {
  const owner = _get(responseData, 'owner.ownerUser', null)
  const pipeline = _get(responseData, 'pipeline', {})
  _set(
    pipeline,
    'stages',
    _get(pipeline, 'stages', []).sort((a: IPipelineStage, b: IPipelineStage) => a.stageOrder - b.stageOrder)
  )
  const pipelineStage = _get(responseData, 'pipelineStage', undefined)
  const isClosed = _get(responseData, 'isClosed')
  const isWon = _get(responseData, 'isWon')

  const fieldsIdentifierArray = ['Name', 'Amount', 'Probability']

  const dealInfo = fieldsIdentifierArray.reduce((acc, curr) => {
    const fieldValue = responseData.fields?.find((item) => item.field.identifier === curr)?.value
    return { ...acc, [_camelCase(curr)]: { ..._get(cachedData, _camelCase(curr)), value: fieldValue } }
  }, {} as Partial<IDeal>)

  const detailsFields = cachedData.detailsFields.reduce((acc, curr) => {
    const fieldName = curr.id.split('_')[0]
    const fieldId = curr.field.id
    if (fieldName === 'multiValues') {
      curr.value = responseData.multiValuesFields?.find((item) => item.field.id === fieldId)?.values
    } else {
      curr.value = responseData.fields?.find((item) => item.field.id === fieldId)?.value
    }
    return [...acc, curr]
  }, [] as IPreferenceField[])

  return {
    ...cachedData,
    owner,
    pipeline,
    pipelineStage,
    isWon,
    isClosed,
    ...dealInfo,
    detailsFields: JSON.parse(JSON.stringify(detailsFields)),
  }
}

export function getOrganizationProfile({
  organizationRawData,
  organizationMetaData,
}: {
  organizationRawData: IRawOrganization
  organizationMetaData: IOrganizationMetadata
}): Partial<IOrganization> {
  const organizationId = _get(organizationRawData, 'id')
  const owner = _get(organizationRawData, 'owner.ownerUser', null)

  const fieldIdentifiersArray = ['Name', 'City', 'State', 'Logo', 'Phone']

  const organizationInfo = fieldIdentifiersArray.reduce((acc, curr) => {
    const fieldValue = organizationRawData.fields?.find((item) => item.field.identifier === curr)?.value
    return { ...acc, [_camelCase(curr)]: fieldValue }
  }, {} as Partial<IOrganization>)

  const detailsFields = organizationMetaData.userPreference.data.reduce((acc, curr) => {
    const field = {} as IPreferenceField
    field.id = `field_${curr}`
    field.field = organizationMetaData.fields.find((item) => item.id === curr) as IField

    if (field.field === undefined) {
      field.field = organizationMetaData.multiValuesFields.find((item) => item.id === curr) as IField
      field.value = organizationRawData.multiValuesFields?.find((item) => item.field.id === curr)?.values
      field.id = `multiValues_${field.field.id}`
    } else field.value = organizationRawData.fields?.find((item) => item.field.id === curr)?.value

    return [...acc, field]
  }, [] as IPreferenceField[])

  return { id: organizationId, owner, detailsFields, ...organizationInfo }
}

export function filterOrganizationPatchResponse({
  responseData,
  cachedData,
}: {
  responseData: Partial<IRawOrganization>
  cachedData: IOrganization
}): Partial<IOrganization> {
  const owner = _get(responseData, 'owner.ownerUser', null)

  const fieldsIdentifierArray = ['Name', 'City', 'State', 'Logo', 'Phone']

  const organizationInfo = fieldsIdentifierArray.reduce((acc, curr) => {
    const fieldValue = responseData.fields?.find((item) => item.field.identifier === curr)?.value
    return { ...acc, [_camelCase(curr)]: fieldValue }
  }, {} as Partial<IOrganization>)

  const detailsFields = cachedData.detailsFields.reduce((acc, curr) => {
    const fieldName = curr.id.split('_')[0]
    const fieldId = curr.field.id
    if (fieldName === 'multiValues') {
      curr.value = responseData.multiValuesFields?.find((item) => item.field.id === fieldId)?.values
    } else {
      curr.value = responseData.fields?.find((item) => item.field.id === fieldId)?.value
    }
    return [...acc, curr]
  }, [] as IPreferenceField[])

  return {
    ...cachedData,
    owner,
    ...organizationInfo,
    detailsFields: JSON.parse(JSON.stringify(detailsFields)),
  }
}
